import React, { useCallback, useEffect, useState } from "react";
import { getSiteSettings, getSites } from "../../services/sites";
import { SETUP_CHANGED_EVENT, supportLink, supportMessage } from "../../utils/support";
import { openSettingsSection } from "../../utils/settingsLink";
import { useOwnezzyContact } from "../../utils/branding";
import FloatingPanel from "../../components/FloatingPanel";

// What each setup step asks for. The backend decides whether a step is done (utils/storeSetup.js);
// `section` is the Store settings card the "Go" link opens (Store settings tab, see AdminPanel).
const STEPS = {
    payment: {
        title: "Connect Razorpay for UPI payments",
        text: "Add your Razorpay keys so shoppers can pay by UPI (PhonePe, Google Pay, any UPI app or QR) straight into your account. During the free trial your store takes demo payments; on a paid plan it needs your keys, or it takes Cash on Delivery only.",
        section: "store-payment",
        action: "Add Razorpay keys",
        help: "please help me connect Razorpay for UPI payments in my store",
    },
    shippingTax: {
        title: "Confirm shipping charges and GST",
        text: "Check your delivery charge, the order amount above which shipping is free (₹0), and your GST rate, then save them.",
        section: "store-shipping",
        action: "Update shipping & GST",
    },
    business: {
        title: "Complete your business information",
        text: "Add your name, store name, email, contact number and business address. Shoppers see these on your store and orders.",
        section: "store-business",
        action: "Update business info",
    },
};

// Red banner at the top of the admin panel for store owners, shown until every setup step of every store is done.
// One store: its steps. Several: one banner with a row per store still to finish (the first one open).
// On Overview it is in the page; on other tabs (floating) it is a ⚠ button with the steps left (FloatingPanel).
export default function SetupChecklist({ user, floating = false, open = false, onToggle }) {
    const contact = useOwnezzyContact();
    const [stores, setStores] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await getSites({ limit: 100, sort: "name" });
            const withSetup = await Promise.all(
                data.sites.map(async (site) => ({ id: site.id, name: site.name, setup: (await getSiteSettings(site.id)).data.settings.setup }))
            );
            setStores(withSetup);
        } catch {
            setStores([]); // the checklist is a reminder; if it cannot load, the rest of the panel still works
        }
    }, []);

    useEffect(() => {
        load();
        window.addEventListener(SETUP_CHANGED_EVENT, load);
        return () => window.removeEventListener(SETUP_CHANGED_EVENT, load);
    }, [load]);

    const pending = stores.filter((store) => store.setup.some((step) => !step.done));
    if (!pending.length) return null;

    const progress = (store) => ({ done: store.setup.filter((step) => step.done).length, total: store.setup.length });
    const foot = (storeName) => (
        <p className="setup-foot">
            Need help or have a question? <a href={supportLink(supportMessage(user, `I have a question about setting up my store "${storeName}".`), contact.whatsapp)} target="_blank" rel="noreferrer">Reach out to support on WhatsApp</a>.
        </p>
    );

    const left = pending.reduce((n, store) => n + store.setup.filter((step) => !step.done).length, 0);
    const wrap = (banner) => (
        <FloatingPanel floating={floating} open={open} onToggle={onToggle} slot={1} tone="is-warn" icon="⚠" label="Finish setting up your store" badge={left}>
            {banner}
        </FloatingPanel>
    );

    if (stores.length === 1) {
        const [store] = pending;
        const { done, total } = progress(store);
        return wrap(
            <section className="setup-alert" role="alert" aria-label={`Setup for ${store.name}`}>
                <div className="setup-alert-head">
                    <div>
                        <h2>⚠ Finish setting up your store</h2>
                        <p>These steps stay here until they are done.</p>
                    </div>
                    <ProgressBar done={done} total={total} label={`${done} of ${total} completed`} />
                </div>
                <Steps store={store} user={user} />
                {foot(store.name)}
            </section>
        );
    }

    const ready = stores.length - pending.length;
    return wrap(
        <section className="setup-alert" role="alert" aria-label="Setup for your stores">
            <div className="setup-alert-head">
                <div>
                    <h2>⚠ Finish setting up your stores</h2>
                    <p>{pending.length} of your {stores.length} stores still need a few steps. They stay here until they are done.</p>
                </div>
                <ProgressBar done={ready} total={stores.length} label={`${ready} of ${stores.length} stores ready`} />
            </div>
            <div className="setup-store-list">
                {pending.map((store, index) => {
                    const { done, total } = progress(store);
                    return (
                        <details className="setup-store" key={store.id} open={index === 0}>
                            <summary>
                                <b>{store.name}</b>
                                <span className="setup-badge pending">{done} of {total} done</span>
                            </summary>
                            <Steps store={store} user={user} />
                        </details>
                    );
                })}
            </div>
            {foot(pending[0].name)}
        </section>
    );
}

function ProgressBar({ done, total, label }) {
    return (
        <div className="setup-progress" aria-label={label}>
            <b>{label}</b>
            <span className="setup-progress-bar"><i style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></span>
        </div>
    );
}

// One store's steps, each done or with a link to the settings that finish it
function Steps({ store, user }) {
    const contact = useOwnezzyContact();
    return (
        <ol className="setup-steps">
            {store.setup.map((step) => {
                const info = STEPS[step.key];
                if (!info) return null;
                return (
                    <li key={step.key} className={step.done ? "is-done" : "is-pending"}>
                        <span className="setup-status" aria-hidden="true">{step.done ? "✓" : "!"}</span>
                        <div className="setup-step-copy">
                            <b>{info.title}</b>
                            {!step.done && <p>{info.text}</p>}
                        </div>
                        {step.done ? (
                            <span className="setup-badge done">Completed</span>
                        ) : (
                            <div className="setup-step-actions">
                                <span className="setup-badge pending">Not completed</span>
                                <a className="setup-go" href={`#${info.section}`} onClick={openSettingsSection}>{info.action} →</a>
                                {info.help && <a className="setup-help" href={supportLink(supportMessage(user, `${info.help} "${store.name}".`), contact.whatsapp)} target="_blank" rel="noreferrer">Ask support</a>}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}